import React, { useEffect, useRef } from 'react';
import Icon from 'components/AppIcon';

const TOAST_ICONS = {
  success: { name: 'CheckCircle', color: 'var(--color-success)' },
  error: { name: 'XCircle', color: 'var(--color-error)' },
  warning: { name: 'AlertTriangle', color: 'var(--color-warning)' },
  info: { name: 'Info', color: 'var(--color-primary)' },
};

const TOAST_BORDER_COLORS = {
  success: 'var(--color-success)',
  error: 'var(--color-error)',
  warning: 'var(--color-warning)',
  info: 'var(--color-primary)',
};

// Single Toast Item
function ToastItem({ toast, onDismiss }) {
  const timerRef = useRef(null);
  const icon = TOAST_ICONS?.[toast?.type] || TOAST_ICONS?.info;
  const borderColor = TOAST_BORDER_COLORS?.[toast?.type] || TOAST_BORDER_COLORS?.info;

  useEffect(() => {
    if (toast?.duration !== 0) {
      timerRef.current = setTimeout(() => {
        onDismiss(toast?.id);
      }, toast?.duration || 4000);
    }
    return () => clearTimeout(timerRef?.current);
  }, [toast?.id, toast?.duration, onDismiss]);

  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex items-start gap-3 bg-card border border-border rounded-lg px-4 py-3 w-full max-w-sm pointer-events-auto"
      style={{
        boxShadow: '0 8px 24px rgba(28,25,23,0.14)',
        borderLeft: `3px solid ${borderColor}`,
        animation: 'slideInRight 250ms ease-out',
      }}
    >
      <div className="flex-shrink-0 mt-0.5">
        <Icon name={icon?.name} size={18} color={icon?.color} strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        {toast?.title && (
          <p className="text-sm font-caption font-medium text-foreground leading-tight">
            {toast?.title}
          </p>
        )}
        {toast?.message && (
          <p className="text-sm font-caption text-muted-foreground leading-snug mt-0.5">
            {toast?.message}
          </p>
        )}
      </div>
      <button
        onClick={() => onDismiss(toast?.id)}
        className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded hover:bg-muted transition-all duration-[250ms] ease-out focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
        aria-label="Cerrar notificación"
      >
        <Icon name="X" size={14} color="var(--color-muted-foreground)" />
      </button>
    </div>
  );
}

// Toast Container - renders the queue
export default function NotificationToast({ toasts = [], onDismiss }) {
  if (!toasts?.length) return null;

  return (
    <div
      className="fixed top-20 right-4 z-toast flex flex-col gap-2 pointer-events-none"
      aria-label="Notificaciones"
      style={{ maxWidth: '360px', width: 'calc(100vw - 32px)' }}
    >
      {toasts?.map((toast) => (
        <ToastItem key={toast?.id} toast={toast} onDismiss={onDismiss} />
      ))}
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(24px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}